import { useState, useMemo } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useShowHiddenNotes } from './useShowHiddenNotes'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  GovUKPageContainer,
  GovUKTypography,
  GovUKButton,
  GovUKInput,
  GovUKContentBox,
  GovUKLink,
  GovUKGrid,
  GovUKGridColumn,
} from '@/components/govuk'
import { GOVUK_COLOURS } from '@/components/govuk/colours'
import { getNotes } from '@/data/notes/getNotes'
import { deleteNote } from '@/data/notes/deleteNote'
import { queryKeys } from '@/lib/queryKeys'
import { useCurrentUser } from './CurrentUserContext'
import type { NoteListItem } from '@/data/notes/noteSchemas'
import 'govuk-frontend/dist/govuk/govuk-frontend.min.css'

function timeAgo(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 30) return `${days}d ago`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months}mo ago`
  return `${Math.floor(months / 12)}y ago`
}

interface NoteCardProps {
  note: NoteListItem
  onDelete: (id: string) => void
  accent?: boolean
}

function NoteCard({ note, onDelete, accent = false }: NoteCardProps) {
  const [confirming, setConfirming] = useState(false)

  const cardStyle: React.CSSProperties = accent
    ? {
        borderLeft: `4px solid ${GOVUK_COLOURS.blue}`,
        borderTop: 'none',
        borderRight: 'none',
        borderBottom: 'none',
        paddingLeft: '16px',
        backgroundColor: GOVUK_COLOURS.lightGrey,
      }
    : {}

  return (
    <GovUKContentBox style={cardStyle}>
      <GovUKTypography variant="heading-s" marginBottom={1}>
        <GovUKLink to="/notes/$noteId" params={{ noteId: note.id }}>
          {note.title || 'Untitled'}
        </GovUKLink>
      </GovUKTypography>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
        <GovUKTypography variant="body-s" style={{ color: GOVUK_COLOURS.darkGrey, margin: 0 }}>
          Updated {timeAgo(note.updatedAt)}
        </GovUKTypography>

        {confirming ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <GovUKTypography variant="body-s" style={{ margin: 0, color: GOVUK_COLOURS.red }}>
              Delete this note?
            </GovUKTypography>
            <GovUKButton variant="warning" style={{ marginBottom: 0, padding: '4px 12px', fontSize: '14px' }} onClick={() => onDelete(note.id)}>
              Yes, delete
            </GovUKButton>
            <GovUKButton variant="secondary" style={{ marginBottom: 0, padding: '4px 12px', fontSize: '14px' }} onClick={() => setConfirming(false)}>
              Cancel
            </GovUKButton>
          </div>
        ) : (
          <button
            onClick={() => setConfirming(true)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: GOVUK_COLOURS.red, fontSize: '14px', padding: '2px 0', textDecoration: 'underline' }}
          >
            Delete
          </button>
        )}
      </div>
    </GovUKContentBox>
  )
}

export function NotesListPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { currentUser } = useCurrentUser()
  const [search, setSearch] = useState('')
  const showHidden = useShowHiddenNotes()

  const { data: notes = [], isLoading } = useQuery({
    queryKey: queryKeys.notes.lists(),
    queryFn: () => getNotes({ data: { currentUser: currentUser ?? '' } }),
  })

  const deleteMutation = useMutation({
    mutationFn: (noteId: string) => deleteNote({ data: { noteId } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.notes.all }),
  })

  const filtered = useMemo(() => {
    const visible = showHidden ? notes : notes.filter((n) => !n.hidden)
    const q = search.toLowerCase().trim()
    if (!q) return visible
    return visible.filter((n) => n.title.toLowerCase().includes(q))
  }, [notes, search, showHidden])

  const recentNotes = filtered.slice(0, 3)
  const olderNotes = filtered.slice(3)
  const showSections = filtered.length >= 4 && !search

  return (
    <GovUKPageContainer>
      <GovUKGrid>
        <GovUKGridColumn width="full">
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' }}>
            <GovUKTypography variant="heading-xl" marginBottom={0}>Notes</GovUKTypography>
            <GovUKButton onClick={() => navigate({ to: '/notes/new' })}>+ New note</GovUKButton>
          </div>

          {/* Loading */}
          {isLoading && (
            <GovUKTypography variant="body" style={{ color: GOVUK_COLOURS.darkGrey }}>Loading notes…</GovUKTypography>
          )}

          {/* Search */}
          {!isLoading && notes.length > 0 && (
            <div style={{ marginBottom: '24px', maxWidth: '400px' }}>
              <GovUKInput id="notes-search" placeholder="Search notes…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          )}

          {/* Empty state */}
          {!isLoading && notes.length === 0 && (
            <GovUKContentBox style={{ textAlign: 'center', padding: '40px 20px' }}>
              <GovUKTypography variant="heading-m" marginBottom={2}>No notes yet</GovUKTypography>
              <GovUKTypography variant="body" marginBottom={4}>Create your first note to get started.</GovUKTypography>
              <GovUKButton onClick={() => navigate({ to: '/notes/new' })}>Create a note</GovUKButton>
            </GovUKContentBox>
          )}

          {/* No search results */}
          {!isLoading && notes.length > 0 && filtered.length === 0 && (
            <GovUKTypography variant="body" style={{ color: GOVUK_COLOURS.darkGrey }}>No notes match "{search}"</GovUKTypography>
          )}

          {/* Recent + All sections */}
          {showSections && (
            <>
              <GovUKTypography variant="heading-m" marginBottom={3}>Recent</GovUKTypography>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '32px' }}>
                {recentNotes.map((note) => <NoteCard key={note.id} note={note} onDelete={(id) => deleteMutation.mutate(id)} accent />)}
              </div>
              <GovUKTypography variant="heading-m" marginBottom={3}>All notes</GovUKTypography>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {olderNotes.map((note) => <NoteCard key={note.id} note={note} onDelete={(id) => deleteMutation.mutate(id)} />)}
              </div>
            </>
          )}

          {/* Flat list */}
          {!showSections && filtered.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {filtered.map((note) => <NoteCard key={note.id} note={note} onDelete={(id) => deleteMutation.mutate(id)} accent />)}
            </div>
          )}
        </GovUKGridColumn>
      </GovUKGrid>
    </GovUKPageContainer>
  )
}
